import { createContext, useContext, useState, type ReactNode } from "react";
import { translations } from "@/lib/translations";

export type Language = "sv" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem("zensio-language");
    return (stored === "en" ? "en" : "sv") as Language;
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("zensio-language", lang);
  };

  const t = (key: string): string => {
    return translations[key]?.[language] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Fallback so components rendered outside the provider (or during HMR
    // when the context identity is swapped) don't crash the whole app.
    const stored =
      typeof window !== "undefined" ? localStorage.getItem("zensio-language") : null;
    const language: Language = stored === "en" ? "en" : "sv";
    return {
      language,
      setLanguage: (lang: Language) => {
        if (typeof window !== "undefined") {
          localStorage.setItem("zensio-language", lang);
        }
      },
      t: (key: string) => translations[key]?.[language] ?? key,
    };
  }
  return ctx;
}
